import React, { useState } from 'react';
import { UserMoreInfo } from '../../../#interfaces/interfaces';
import { UserAvatar } from '../../userAvatar/UserAvatar';
import { AppDispatch, RootState } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateAvatar } from '../../../features/sessions/sessionSlice';
import { updateGeneralConfig } from '../../../features/sessions/sessionConfig/sessionUserConfigurations';
import * as REQUEST_REQUIREMENTS from '../../../api/requestRequirements';
import { toast } from 'react-toastify';
import {
    Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT_PRIMARY,
        bgcolor: BG_CARD,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px ${BG_CARD} inset`,
            WebkitTextFillColor: TEXT_PRIMARY,
            caretColor: TEXT_PRIMARY,
        },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
};

export const GeneralUserConfigForm: React.FC<{ userMoreInfo: UserMoreInfo }> = ({ userMoreInfo }) => {
    const dispatch  = useDispatch<AppDispatch>();
    const navigate  = useNavigate();
    const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
    const avatar      = useSelector((state: RootState) => state.session.userMoreInfo.avatarUrl);
    const loading     = useSelector((state: RootState) => state.sessionUserConfig.loading);

    const [inputNameValue,        setInputNameValue]        = useState(userMoreInfo.name);
    const [inputLastNameValue,    setInputLastNameValue]    = useState(userMoreInfo.lastName);
    const [inputNicknameValue,    setInputNicknameValue]    = useState(userMoreInfo.nickname);
    const [inputPhoneNumberValue, setInputPhoneNumberValue] = useState(userMoreInfo.phoneNumber);
    const [inputWebsiteValue,     setInputWebsiteValue]     = useState(userMoreInfo.website);
    const [inputCityValue,        setInputCityValue]        = useState(userMoreInfo.city);
    const [inputStateValue,       setInputStateValue]       = useState(userMoreInfo.state);
    const [inputCountryValue,     setInputCountryValue]     = useState(userMoreInfo.country);
    const [inputAboutValue,       setInputAboutValue]       = useState(userMoreInfo.about);
    const [inputEmailValue,       setInputEmailValue]       = useState(userMoreInfo.email);

    async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (authHeaders && userMoreInfo.nickname && file) {
            await dispatch(updateAvatar({ authHeaders, userNickname: userMoreInfo.nickname, patchBody: file }));
        }
    }

    async function handleUpdateGeneralConfig() {
        const patchBody = {
            name: inputNameValue, lastName: inputLastNameValue, nickname: inputNicknameValue,
            email: inputEmailValue, phoneNumber: inputPhoneNumberValue, website: inputWebsiteValue,
            city: inputCityValue, state: inputStateValue, country: inputCountryValue, about: inputAboutValue,
        };
        if (authHeaders && userMoreInfo.nickname) {
            const response = await dispatch(updateGeneralConfig({ authHeaders, userNickname: userMoreInfo.nickname, patchBody }));
            if (response.meta.requestStatus === 'fulfilled') {
                const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({
                    ROUTE_PARAMS: { userNickname: response.payload['user_more_info'].nickname },
                });
                navigate(PRIVATE_ROUTES.USER_CONFIG);
            } else {
                (response.payload as string[]).map((item: string) => toast.error(item));
            }
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: BRAND_RED }} />
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: BG_SECTION, borderRadius: '10px', border: BORDER_SUBTLE, p: { xs: 2, sm: 3 } }}>
            <Typography
                fontWeight={600}
                color={TEXT_PRIMARY}
                mb={{ xs: 2, sm: 3 }}
                sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}
            >
                General Information
            </Typography>

            {/* Avatar section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, borderBottom: BORDER_SUBTLE }}>
                <UserAvatar src={avatar} size={60} sx={{ width: { xs: 52, sm: 64, md: 72 }, height: { xs: 52, sm: 64, md: 72 } }} />
                <Box>
                    <Typography variant='body2' fontWeight={600} color={TEXT_PRIMARY}>Profile Photo</Typography>
                    <Typography variant='caption' color={TEXT_SECONDARY} display='block' mb={1}>
                        JPG or PNG, max 5 MB
                    </Typography>
                    <Button
                        variant='outlined'
                        size='small'
                        startIcon={<CameraAltOutlinedIcon />}
                        onClick={() => document.getElementById('inputAvatar')?.click()}
                        sx={{
                            borderColor: 'rgba(255,255,255,0.2)',
                            color: TEXT_SECONDARY,
                            textTransform: 'none',
                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' },
                        }}
                    >
                        Change photo
                    </Button>
                    <input type='file' id='inputAvatar' accept='image/*' onChange={handleAvatarChange} style={{ display: 'none' }} />
                </Box>
            </Box>

            {/* Fields grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 1.5, sm: 2 } }}>
                <TextField label='Name' value={inputNameValue ?? ''} onChange={e => setInputNameValue(e.target.value)} size='small' sx={inputSx} />
                <TextField label='Last Name' value={inputLastNameValue ?? ''} onChange={e => setInputLastNameValue(e.target.value)} size='small' sx={inputSx} />
                <TextField label='Nickname' value={inputNicknameValue ?? ''} onChange={e => setInputNicknameValue(e.target.value)} size='small' sx={inputSx} />
                <TextField label='Contact Email' value={inputEmailValue ?? ''} onChange={e => setInputEmailValue(e.target.value)} size='small' inputProps={{ maxLength: 320 }} sx={inputSx} />
                <TextField label='Phone Number' value={inputPhoneNumberValue ?? ''} onChange={e => setInputPhoneNumberValue(e.target.value)} size='small' inputProps={{ maxLength: 15 }} sx={inputSx} />
                <TextField label='Website' value={inputWebsiteValue ?? ''} onChange={e => setInputWebsiteValue(e.target.value)} size='small' inputProps={{ maxLength: 320 }} sx={inputSx} />
                <TextField label='Country' value={inputCountryValue ?? ''} onChange={e => setInputCountryValue(e.target.value)} size='small' inputProps={{ maxLength: 52 }} sx={inputSx} />
                <TextField label='State' value={inputStateValue ?? ''} onChange={e => setInputStateValue(e.target.value)} size='small' inputProps={{ maxLength: 3 }} sx={inputSx} />
                <TextField label='City' value={inputCityValue ?? ''} onChange={e => setInputCityValue(e.target.value)} size='small' inputProps={{ maxLength: 20 }} sx={{ ...inputSx, gridColumn: { sm: '1 / -1', md: 'auto' } }} />
                <TextField
                    label='About'
                    value={inputAboutValue ?? ''}
                    onChange={e => setInputAboutValue(e.target.value)}
                    multiline rows={4}
                    inputProps={{ maxLength: 255 }}
                    sx={{ ...inputSx, gridColumn: '1 / -1' }}
                />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: { xs: 2, sm: 3 } }}>
                <Button
                    variant='contained'
                    onClick={handleUpdateGeneralConfig}
                    fullWidth={false}
                    sx={{
                        bgcolor: BRAND_RED,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: { xs: 3, sm: 4 },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        '&:hover': { bgcolor: '#a93226' },
                    }}
                >
                    Save Changes
                </Button>
            </Box>
        </Box>
    );
};
